import React, { useEffect, useState, useContext } from 'react'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Divider } from '@mui/material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Container, VideoPlayer, GoogleAd } from '../'
import { contentUrl } from '../../helpers/url'
import { useAlert, useGoogleAdsense, useFormFactor, usePlaylistNavigation, useGroup } from '../../hooks'
import { contentSlice } from '../../reducers'
import { useAddToBookmarks } from '../add-to-bookmarks'
import { ArticleBodyLoading } from '../article-view/article-view-loading'
import CommentsView from '../comments-view'
import Hide from '../hide'
import { useSEO } from '../seo-context'
import CallToActionMeteringUsage from './cta-metering'
import ContentMetaTags from './meta-tags'
import PreviewPaywall from './preview-paywall'
import DiscoverMoreContent from './discover-more-content'
import { useStyles } from './style'
import { PromotedContentGenericCard } from '../general-card/promoted-content-card'
import { useXapi } from '../../hooks/xapi'
import RelatedArticles from './related-articles'
import { GoogleAdContext } from '../google-ad/google-ad-context'
import { ALL_FREE } from '../../services/cognito/userGroups'

const Body = React.lazy(() => import('./body'))

const Article = props => {
  const { isInGroup } = useGroup()
  const isFreeUser = isInGroup(ALL_FREE)
  const { sendAdsRequest, adsRequestSent } = useContext(GoogleAdContext)

  const content = useSelector(contentSlice.selectors.selectContent)
  const isExclusive = useSelector(contentSlice.selectors.selectIsExclusive)
  const discoverMoreContent = useSelector(contentSlice.selectors.selectDiscoverMoreContent)
  const relatedContent = useSelector(contentSlice.selectors.selectRelatedContent)
  const isPreview = useSelector(contentSlice.selectors.selectIsPreview)
  const showMeteringCallToAction = useSelector(contentSlice.selectors.selectShowMeternigCallToAction)
  const [bookmarksContent, setBookmarksContent] = useState([])
  const [getRelatedContent] = useEvent([
    {
      eventName: 'contentGetRelatedContent',
      onSuccess: [{ redux: true, action: contentSlice.actions.setDiscoverMoreContent }, { redux: true, action: contentSlice.actions.setRelatedContent }],
      onError: () => {
        enqueueMsg('Unable to load related content recommendation', 'error')
      }
    }
  ])

  const navigate = useNavigate()
  const { width, minInset, onAddToPlaylist } = props
  const {
    videoUrl,
    locationId
  } = content

  const classes = useStyles(props)
  const { enqueueMsg } = useAlert()
  const handleAddToPlaylist = article => onAddToPlaylist(article)

  const { isBookmarked, handleBookmark } = useAddToBookmarks(bookmarksContent)

  useEffect(() => {
    if (content) {
      setBookmarksContent(discoverMoreContent ? [...discoverMoreContent, content] : [content])
    }
  }, [discoverMoreContent, content])

  const handleArticleSelect = article => {
    navigate(contentUrl(article, 'discover-more-content'))
  }

  const { useAdsense, contentHasAds } = useGoogleAdsense()

  useEventsOnViewLoad(() => {
    if (locationId && !getRelatedContent.hasBeenTriggered) {
      getRelatedContent.trigger({
        locationId
      })
    }
  }, [locationId, getRelatedContent])

  useSEO(!!discoverMoreContent && !!relatedContent && !getRelatedContent.isWorking)

  useXapi(content)

  const { isDesktop, isLargeDesktop } = useFormFactor()
  const { playlistContextId } = usePlaylistNavigation()

  if (relatedContent.length > 0 && isFreeUser && !adsRequestSent) {
    sendAdsRequest()
  }

  return (
    <>
      <ContentMetaTags content={content} />
      <Container
        maxContentWidth={width}
        inset={minInset}
        classes={{ content: classes.article }}
        className={classes.container}
      >
        <VideoPlayer url={videoUrl} />
        {(useAdsense && contentHasAds(content, isExclusive)) &&
          <GoogleAd
            adKey='content'
            adPosition='header'
            adSource='components>article>index.js'
          />}
        <React.Suspense fallback={<ArticleBodyLoading />}>
          <Body
            body={content?.body}
            className={classes.body}
          />
        </React.Suspense>
        {/* Render paywall content for SEO */}
        {content.paywall && (
          <Hide hidden={isPreview} className='paywall'>
            <React.Suspense fallback={<ArticleBodyLoading />}>
              <Body body={content.paywall} />
            </React.Suspense>
          </Hide>
        )}
        {!isPreview && (
          <>
            {showMeteringCallToAction
              ? <CallToActionMeteringUsage />
              : <Divider className={classes.divider} />}
            {/* <Cite onClick={handleCite} /> */}
            <DiscoverMoreContent
              title='Discover more content'
              renderArticle={article => (
                <PromotedContentGenericCard
                  content={article}
                  onSelect={() => handleArticleSelect(article)}
                  onAddToPlaylist={() => handleAddToPlaylist(article)}
                  onBookmark={() => handleBookmark(article)}
                  bookmarked={isBookmarked(article)}
                />
              )}
            />
            <CommentsView locationId={locationId} />
          </>
        )}
        {!(isDesktop || isLargeDesktop) && !playlistContextId && <RelatedArticles title='Related Articles' />}
      </Container>
      {isPreview && (
        <PreviewPaywall />
      )}
    </>
  )
}

export default Article
